<script setup>
defineProps({
  id: {
    type: String,
    required: true,
  },
})

const { $config, $router } = useNuxtApp()
const emit = defineEmits(['commit'])
const $route = useRoute()
const selectedPlanSlug = ref(null)
const selectedGift = ref(null)
const giftNote = ref('')
const sendDate = ref(moment().format('YYYY-MM-DD'))
const errors = ref([])
const memberships = useMemberships()
const giftableMemberships = computed(() => memberships.giftableMemberships)
const sectionState = computed(() => memberships.sectionState)
const drawerOpen = computed(() => sectionState.value.giftSelection.drawerOpen)
const membershipTypeSlug = computed(() => $route.params.slug)
const membershipType = computed(() =>
  giftableMemberships.value.find((membership) => {
    return membership.slug === membershipTypeSlug.value
  }),
)
const allGiftablePlans = computed(() =>
  membershipType.value?.plans?.filter((plan) => plan.gift_terms.length >= 1),
)
const selectedPlan = computed(() => getPlan(selectedPlanSlug.value))
const giftTerms = computed(() => selectedPlan.value?.gift_terms || [])
const bottlesString = computed(() => {
  if (membershipType.value.bottles_per_delivery) {
    const bottleCount = membershipType.value.bottles_per_delivery
    return `${bottleCount} unique selection${
      bottleCount > 1 ? 's' : ''
    } per shipment.`
  }
  return ''
})
const committedPlan = computed(() =>
  getPlan(sectionState.value.giftSelection.selectedPlan),
)
const committedGift = computed(() =>
  getGift(
    sectionState.value.giftSelection.selectedPlan,
    sectionState.value.giftSelection.selectedGift,
  ),
)
const showPlanSelection = computed(() => membershipType.value.plans.length > 1)
const sectionClasses = computed(() => {
  const classes = ['checkout-details-section']
  if (!drawerOpen.value) {
    classes.push('edit-section')
  }
  return classes
})

onMounted(() => {
  // Pre-select the first plan if it is the only plan
  if (!selectedPlanSlug.value) {
    selectedPlanSlug.value = membershipType.value?.plans?.[0]?.slug
  }
})

function goBack() {
  if ($config.public.hasGiftingLanding) {
    $router.push('/wine-club/gifting/')
  } else {
    $router.go(-1)
  }
}
function clearErrors() {
  errors.value = []
}

async function commit() {
  memberships.setSectionState({
    section: 'giftSelection',
    value: {
      drawerOpen: false,
      selectedPlan: selectedPlanSlug,
      selectedGift: selectedGift,
      giftNote: giftNote.value ? giftNote.value.trim() : '',
      sendDate: sendDate,
      errors: [],
    },
  })
  emit('commit')
}
function getPlan(slug) {
  return membershipType.value.plans.find((plan) => {
    return plan.slug === slug
  })
}
function getGift(planSlug, giftId) {
  const plan = getPlan(planSlug)
  if (!plan) return null
  return plan.gift_terms.find((gift) => {
    return gift.id === giftId
  })
}
function openDrawer() {
  memberships.setSectionDrawerOpen({ section: 'giftSelection', value: true })
}
function closeDrawer() {
  errors.value = []
  memberships.setSectionDrawerOpen({ section: 'giftSelection', value: false })
}
function planSelected() {
  // Pre-select the first gift term if it is the only gift term
  const plan = getPlan(selectedPlanSlug)
  if (plan && plan.gift_terms.length === 1) {
    selectedGift.value = plan.gift_terms[0].id
  }
  clearErrors()
}

watch(selectedPlanSlug, planSelected)
watch(selectedGift, clearErrors)
</script>

<template>
  <CheckoutSection
    :id="id"
    section-title="Gift Selection"
    :show-section-number="true"
    :section-number="1"
    :enabled="true"
    :drawer-open="drawerOpen"
    :show-change-link="true"
    :errors="errors"
    change-text="Edit"
    :classes="sectionClasses"
    @drawerOpen="openDrawer"
    @drawerClosed="closeDrawer"
  >
    <template #collapsed>
      <div class="checkout-section-summary">
        <ul>
          <li>
            <strong>{{ membershipType.name }}</strong>
          </li>
          <li v-if="committedPlan && committedGift">
            {{ committedPlan.name }},
            {{ committedGift.num_of_shipments }} Shipment{{
              committedGift.num_of_shipments > 1 ? 's' : ''
            }}, {{ committedGift.title }} -
            <strong>${{ committedGift.base_price }}</strong>
          </li>
          <li v-if="sectionState.giftSelection.giftNote" class="mt">
            Gift Note:
            <em>{{ sectionState.giftSelection.giftNote }}</em>
          </li>
          <li v-if="sectionState.giftSelection.sendDate" class="mt">
            Notify Giftee On
            <em>{{
              moment(sectionState.giftSelection.sendDate, 'YYYY-MM-DD').format(
                'dddd, MMMM Do',
              )
            }}</em>
          </li>
        </ul>
      </div>
    </template>
    <template #expanded>
      <div class="checkout-section-content">
        <FormKit type="form" :actions="false" @submit="commit">
          <div class="gift-selection">
            <h3>
              <span class="club-name">{{ membershipType.name }}</span>
              <button class="change-club" @click="goBack">
                Select a Different Club
              </button>
            </h3>
            <div v-html="membershipType?.gifting_blurb" />
            <p>
              <em
                >${{
                  Math.trunc(Number(membershipType.plans[0].shipment_price))
                }}
                + tax per shipment. {{ bottlesString }}</em
              >
            </p>
            <div class="radio-grid stacked small">
              <h4 v-if="showPlanSelection">Gift Type</h4>
              <ul v-if="showPlanSelection">
                <li v-for="plan in allGiftablePlans" :key="plan.slug">
                  <input
                    :id="plan.slug"
                    v-model="selectedPlanSlug"
                    type="radio"
                    name="selectedPlanSlug"
                    :value="plan.slug"
                  />
                  <label :for="plan.slug">
                    <strong>
                      {{ plan.name }}
                    </strong>
                  </label>
                </li>
                <FormKit
                  v-model="selectedPlanSlug"
                  type="text"
                  outer-class="hidden-field"
                  validation="required"
                  :validation-messages="{
                    required: 'Error: Please select a gift type.',
                  }"
                />
              </ul>
              <h4 v-if="selectedPlanSlug">Gift Options</h4>
              <ul v-if="selectedPlanSlug">
                <li
                  v-for="gift in giftTerms"
                  :key="selectedPlanSlug + '_' + gift.id"
                >
                  <input
                    :id="selectedPlanSlug + '_' + gift.id"
                    v-model="selectedGift"
                    type="radio"
                    name="selectedGift"
                    :value="gift.id"
                  />
                  <label :for="selectedPlanSlug + '_' + gift.id">
                    <strong>
                      {{ gift.num_of_shipments }} Shipment{{
                        gift.num_of_shipments > 1 ? 's' : ''
                      }}
                    </strong>
                    {{ gift.title }}
                    <span class="note">
                      <Price :amt="gift.base_price" />
                    </span>
                  </label>
                </li>
              </ul>
              <FormKit
                v-model="selectedGift"
                type="text"
                outer-class="hidden-field"
                validation="required"
                :validation-messages="{
                  required: 'Error: Please select a gift option.',
                }"
              />
            </div>
            <div class="giftnote">
              <div class="standard-form">
                <label class="giftnote-label" for="giftnote">Gift Note</label>
                <p class="giftnote-callout">
                  We’ll notify the lucky recipient on your selected date.
                </p>
                <FormKit
                  id="giftnote"
                  v-model="giftNote"
                  type="textarea"
                  maxlength="250"
                  validation="required|length:0,250"
                  :validation-messages="{
                    required: 'Error: Please enter a gift note.',
                    length:
                      'Error: Gift note must be less than 250 characters.',
                  }"
                />
                <span class="textarea-count"
                  >{{ giftNote.length ? giftNote.length : 0 }}/250</span
                >
              </div>
            </div>
            <div class="giftdate">
              <div class="standard-form">
                <div class="giftdate-label">Email Send Date</div>
                <p class="giftdate-callout">
                  You can choose any day in the next twelve weeks to notify your
                  giftee.
                </p>
                <div class="datepicker-container">
                  <FormKit
                    id="send_date"
                    v-model="sendDate"
                    type="date"
                    :validation="
                      'required|date:YYYY-MM-DD|date_before:' +
                      moment()
                        .add(12, 'weeks')
                        .add(1, 'day')
                        .format('YYYY-MM-DD') +
                      '|date_after:' +
                      moment().subtract(1, 'day').format('YYYY-MM-DD')
                    "
                    :validation-messages="{
                      required: 'Error: Please enter a valid send date.',
                      date: 'Error: Please enter a valid send date.',
                      date_after: 'Error: Send date must be today or later.',
                      date_before:
                        'Error: Send date must be in the next twelve weeks.',
                    }"
                    :min="moment().format('YYYY-MM-DD')"
                    :max="moment().add(12, 'weeks').format('YYYY-MM-DD')"
                  />
                  <label
                    for="send_date"
                    class="label"
                    aria-label="Email Send Date"
                  ></label>
                </div>
              </div>
            </div>
            <button
              class="primary left"
              style="margin-bottom: 10px"
              type="submit"
            >
              Continue
            </button>
          </div>
        </FormKit>
      </div>
    </template>
  </CheckoutSection>
</template>
